/**
 * ArcGIS OAuth from the sync panel inside monday.com Map view (iframe).
 * Esri blocks login in iframes — use a popup that postMessages back to the board.
 */

export const ARCGIS_OAUTH_MESSAGE_TYPE = 'arcgis-oauth';

export type ArcGisOAuthMessage = {
  type: typeof ARCGIS_OAUTH_MESSAGE_TYPE;
  status: 'success' | 'error';
  layerId: string;
};

export function buildArcGisOAuthAuthorizePath(
  layerId: string,
  returnBoard?: string,
  popup?: boolean,
): string {
  const params = new URLSearchParams({ layerId });
  if (returnBoard) params.set('returnBoard', returnBoard);
  if (popup) params.set('popup', '1');
  return `/api/sync/arcgis/oauth/authorize?${params.toString()}`;
}

export function isEmbeddedFrame(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Opens OAuth (popup in Monday iframe; same window otherwise). */
export function startArcGisOAuth(layerId: string, returnBoard?: string): void {
  const embedded = isEmbeddedFrame();
  const path = buildArcGisOAuthAuthorizePath(layerId, returnBoard, embedded);
  const url = new URL(path, window.location.origin).href;

  if (embedded) {
    // Do not use noopener — we need window.opener for postMessage after callback.
    window.open(url, 'monday-gis-arcgis-oauth', 'width=520,height=720');
    return;
  }

  window.location.assign(url);
}

export function subscribeArcGisOAuthResult(
  onResult: (msg: ArcGisOAuthMessage) => void,
): () => void {
  const handler = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return;
    const data = event.data as ArcGisOAuthMessage | undefined;
    if (!data || data.type !== ARCGIS_OAUTH_MESSAGE_TYPE) return;
    onResult(data);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
